import { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import styles from './ClassInfo.module.css';

import { useAppDispatch, useAppSelector } from '../../../hooks/useRedux';
import useModal from '../../../hooks/useModal';

import {
  selectAllStudents,
  studentApiSlice,
  useGetStudentQuery,
} from '../../../services/student';
import {
  groupApiSlice,
  selectAllGroup,
  useGetGroupQuery,
} from '../../../services/group';

import {
  classInfoTypeChanged,
  groupListChanged,
  groupPageChanged,
  groupTotalCntSet,
  selectClassInfoType,
  selectGroupList,
  selectGroupPage,
  selectGroupSearch,
  selectGroupTotalCnt,
  selectMaxStudent,
  selectStudentList,
  selectStudentPage,
  selectStudentSearch,
  selectStudentTotalCnt,
  studentAddCntChanged,
  studentListChanged,
  studentPageChanged,
  studentTotalCntSet,
} from '../../../features/classroomSlice';

import Search from '../Search';
import Table from '../Table';

import {
  ADD_GROUP,
  ADD_STUDENT,
  CLASS_INFORMATION,
  DETAIL,
  EMAIL,
  GRADE,
  GROUP,
  INCREASE_CLASS_CAPACITY,
  MANAGE_GROUP,
  MANAGE_STUDENT,
  NAME,
  NICKNAME,
  SCHOOL_GRADE,
  SCHOOL_NAME,
  SETTINGS,
  STUDENT_NUMBER,
} from '../../../constants';
import { isMobile } from '../../../utils/util';
import { useNavigate } from 'react-router-dom';

export default function ClassInfo() {
  const cx = classNames.bind(styles);
  const dispatch = useAppDispatch();
  const { toggleModal } = useModal();
  const navigate = useNavigate();

  const classInfoType = useAppSelector(selectClassInfoType);
  const maxStudent = useAppSelector(selectMaxStudent);

  const [type, setType] = useState(classInfoType);
  const [isShowSearchInput, setIsShowSearchInput] = useState(false);
  const isStudent = type === 'student';

  const info: any = {
    student: {
      page: useAppSelector(selectStudentPage),
      keyword: useAppSelector(selectStudentSearch),
      lazyQuery: studentApiSlice.useLazyGetStudentQuery,
      entities: useAppSelector(selectAllStudents),
      list: useAppSelector(selectStudentList),
      totalCnt: useAppSelector(selectStudentTotalCnt),
      listChanged: studentListChanged,
      pageChanged: studentPageChanged,
      totalCntSet: studentTotalCntSet,
    },
    group: {
      page: useAppSelector(selectGroupPage),
      keyword: useAppSelector(selectGroupSearch),
      lazyQuery: groupApiSlice.useLazyGetGroupQuery,
      entities: useAppSelector(selectAllGroup),
      list: useAppSelector(selectGroupList),
      totalCnt: useAppSelector(selectGroupTotalCnt),
      listChanged: groupListChanged,
      pageChanged: groupPageChanged,
      totalCntSet: groupTotalCntSet,
    },
  };

  const {
    page,
    keyword,
    lazyQuery,
    entities,
    list,
    totalCnt,
    listChanged,
    pageChanged,
    totalCntSet,
  } = info[type];
  const [getList] = lazyQuery();

  const { data: studentData, isLoading: isLoadingStudent } = useGetStudentQuery(
    {
      page: 0,
      search: keyword,
    }
  );
  const { data: groupData, isLoading: isLoadingGroup } = useGetGroupQuery({
    page: 0,
    search: keyword,
  });

  const headerList = isStudent
    ? [
        'nm',
        'alias',
        'schoolTypeNm',
        'schoolNm',
        'gradeTypeNm',
        'email',
        'groupList',
        'dispStatus',
        'details',
      ]
    : ['nm', 'cnt', 'dispStatus', 'details'];

  const headerName = {
    nm: isStudent ? NAME : GROUP,
    alias: NICKNAME,
    schoolTypeNm: SCHOOL_GRADE,
    schoolNm: SCHOOL_NAME,
    gradeTypeNm: GRADE,
    email: EMAIL,
    groupList: GROUP,
    dispStatus: SETTINGS,
    cnt: STUDENT_NUMBER,
    details: DETAIL,
  };

  const selectType = (type: 'student' | 'group') => {
    setType(type);
    dispatch(classInfoTypeChanged(type));
    dispatch(pageChanged(0));
  };

  const updateList = async () => {
    try {
      await getList({
        page,
        search: keyword,
      }).unwrap();
    } catch (error) {
      console.error('Fail to get list', error);
    }
  };

  const addList = () => {
    toggleModal({
      id: isStudent ? 'addStudent' : 'addGroup',
      type: 'alert',
      isOpen: true,
      initialButtonDisabled: true,
    });
  };

  const toggleAddButton = () => {
    setIsShowSearchInput(!isShowSearchInput);
  };

  useEffect(() => {
    updateList();
  }, [page, keyword, type]);

  useEffect(() => {
    if (!totalCnt) {
      const cnt = (isStudent ? studentData : groupData)?.totalCnt;
      dispatch(totalCntSet(cnt));
    }
    isStudent && dispatch(studentAddCntChanged(studentData?.addCnt));
    dispatch(listChanged(entities));
  }, [entities]);

  if (isLoadingStudent || isLoadingGroup) return null;

  return (
    <>
      <h2>{CLASS_INFORMATION}</h2>
      <div className={cx('nav_sub_items')}>
        <span
          className={cx('btn', 'items', { on: isStudent })}
          onClick={() => selectType('student')}
        >
          {MANAGE_STUDENT}
        </span>
        <span
          className={cx('btn', 'items', { on: !isStudent })}
          onClick={() => selectType('group')}
        >
          {MANAGE_GROUP}
        </span>
      </div>

      <div className={cx('option_area')}>
        <Search
          type={type}
          keyword={keyword}
          total={totalCnt}
          toggleAddButton={toggleAddButton}
        />
        {
          <div className={cx('btn_wrap')}>
            {isStudent && totalCnt >= maxStudent && !isShowSearchInput && (
              <button
                className={cx('btn', 'skyt', 'add_class_student')}
                onClick={() => navigate('/home/settings/membership')}
              >
                <span>{INCREASE_CLASS_CAPACITY}</span>
              </button>
            )}

            {(!isMobile || !isShowSearchInput) && (
              <button
                className={cx('btn', 'b_t', 'dull', 'add', {
                  'is--disabled': isStudent && totalCnt >= maxStudent,
                })}
                onClick={addList}
              >
                <span>{isStudent ? ADD_STUDENT : ADD_GROUP}</span>
              </button>
            )}
          </div>
        }
      </div>

      <Table
        name={type}
        headerList={headerList}
        headerName={headerName}
        list={list}
        updateList={updateList}
        pageChanged={pageChanged}
      />
    </>
  );
}
